import { Node } from '@tiptap/core';
import { blockNodeSpec, dataAttribute } from './block';
import { blockViews, initialView } from './block_views';

// Shopify buy button: a live product or collection widget from the creator's Shopify store. Saved as a bare
// block placeholder with its settings as data-shopify-* attributes (the store connection, a few styling
// fields, and optionally the `options` JSON Shopify's Buy Button generator produces);
// Newsletters::RenderBlocks renders it. The web page loads Shopify's SDK (shopify_buy_button_controller.js);
// email gets a placeholder, and the apps get a plain link to the optional product or collection URL. The server re-validates every value (Block::Newsletter::ShopifyBuyButtonComponent),
// so the checks here only tell the creator what will be ignored. The editor chrome is built here, with no
// server template or preview: the widget only runs on the published page. Like the other blocks it shows one
// view at a time (block_views.js): the editor with a Done button, then a collapsed summary with an Edit link.

const DOMAIN = /^[a-z0-9-]+\.myshopify\.com$/;
const RESOURCE_ID = /^\d{1,20}$/;
const TOKEN = /^[0-9a-fA-F]{32}$/;
const COLOUR = /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const FONT = /^[A-Za-z0-9 ]{1,40}$/;
const OPTIONS_MAX_BYTES = 8 * 1024;
// Add to cart opens a cart drawer the Supercast apps can't show; buttons open the product or go to checkout.
const ALLOWED_DESTINATIONS = ['modal', 'checkout'];

const FIELDS = {
  type: 'shopifyType',
  domain: 'shopifyDomain',
  token: 'shopifyToken',
  'resource-id': 'shopifyResourceId',
  url: 'shopifyUrl',
  'accent-color': 'shopifyAccentColor',
  'font-family': 'shopifyFontFamily',
  'add-to-cart-label': 'shopifyAddToCartLabel',
  'cart-title': 'shopifyCartTitle',
  'cart-total-label': 'shopifyCartTotalLabel',
  'money-format': 'shopifyMoneyFormat',
  options: 'shopifyOptions',
};

function addsToCart(options) {
  return Object.values(options).some(section => {
    if (section === null || typeof section !== 'object') return false;
    return [section.buttonDestination].flat().some(destination => destination !== undefined && !ALLOWED_DESTINATIONS.includes(destination));
  });
}

function isHttpsUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && Boolean(url.hostname) && !url.username && !url.password;
  } catch (_) {
    return false;
  }
}

// What the server will ignore, keyed by field name. Empty optional fields are fine.
export function shopifyFieldErrors(attrs) {
  const errors = {};
  const domain = (attrs.shopifyDomain || '').trim().toLowerCase();
  if (domain && !DOMAIN.test(domain)) errors.domain = 'Use your store’s myshopify.com address.';
  const resourceId = (attrs.shopifyResourceId || '').trim();
  if (resourceId && !RESOURCE_ID.test(resourceId)) errors['resource-id'] = 'Use the number from the product or collection ID.';
  const url = (attrs.shopifyUrl || '').trim();
  if (url && !isHttpsUrl(url)) errors.url = 'Use a full https:// link to the product or collection.';
  const token = (attrs.shopifyToken || '').trim();
  if (token && !TOKEN.test(token)) errors.token = 'A storefront token is 32 letters and numbers.';
  const colour = (attrs.shopifyAccentColor || '').trim();
  if (colour && !COLOUR.test(colour)) errors['accent-color'] = 'Use a hex colour, like #b81517.';
  const font = (attrs.shopifyFontFamily || '').trim();
  if (font && !FONT.test(font)) errors['font-family'] = 'Use a Google Font name, like Montserrat.';
  const options = (attrs.shopifyOptions || '').trim();
  if (options) {
    if (new Blob([options]).size > OPTIONS_MAX_BYTES) {
      errors.options = 'Too long. Only the styles, text and layout are needed.';
    } else {
      try {
        const parsed = JSON.parse(options);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          errors.options = 'Paste the options object.';
        } else if (addsToCart(parsed)) {
          errors.options = 'Add to cart isn’t supported. Set buttonDestination to "modal" or "checkout".';
        }
      } catch (_) {
        errors.options = 'That isn’t valid JSON.';
      }
    }
  }
  return errors;
}

const FIELD_CLASS = 'w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:outline-none focus:ring-0';

const EDITOR_HTML = `
  <div class="my-3 border border-gray-200 rounded-lg overflow-hidden bg-white whitespace-normal select-none">
    <div class="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-200">
      <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Shopify buy button</div>
      <div class="flex items-center gap-3 text-xs">
        <a href="#" role="button" draggable="false" class="hidden text-gray-500 hover:text-gray-900" data-edit>
          <span class="inline-flex items-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
            <span>Edit</span>
          </span>
        </a>
        <button type="button" class="text-gray-400 hover:text-red-500" data-remove>Remove</button>
      </div>
    </div>
    <div class="p-4 space-y-4" data-view="editor">
      <div class="flex gap-3">
        <select aria-label="Type" class="${FIELD_CLASS} !w-auto shrink-0 pr-8" data-field="type">
          <option value="product">Product</option>
          <option value="collection">Collection</option>
        </select>
        <input type="text" inputmode="numeric" placeholder="Product or collection ID" aria-label="Product or collection ID"
               class="${FIELD_CLASS} min-w-0 flex-1 font-mono text-xs" data-field="resource-id">
      </div>
      <p class="hidden text-xs text-red-600" data-error="resource-id"></p>
      <input type="text" placeholder="your-store.myshopify.com" aria-label="Store address"
             class="${FIELD_CLASS} font-mono text-xs" data-field="domain">
      <p class="hidden text-xs text-red-600" data-error="domain"></p>
      <input type="text" placeholder="Storefront access token" aria-label="Storefront access token" autocomplete="off"
             class="${FIELD_CLASS} font-mono text-xs" data-field="token">
      <p class="hidden text-xs text-red-600" data-error="token"></p>
      <input type="url" placeholder="Product or collection link (shown in the apps)" aria-label="Product or collection link"
             class="${FIELD_CLASS} font-mono text-xs" data-field="url">
      <p class="hidden text-xs text-red-600" data-error="url"></p>

      <details>
        <summary class="cursor-pointer text-xs font-semibold text-gray-500">Styling</summary>
        <div class="mt-3 space-y-3">
          <div class="flex gap-3">
            <input type="text" placeholder="Button colour, like #b81517" aria-label="Button colour"
                   class="${FIELD_CLASS} font-mono text-xs" data-field="accent-color">
            <input type="text" placeholder="Font, like Montserrat" aria-label="Font"
                   class="${FIELD_CLASS}" data-field="font-family">
          </div>
          <p class="hidden text-xs text-red-600" data-error="accent-color"></p>
          <p class="hidden text-xs text-red-600" data-error="font-family"></p>
          <input type="text" maxlength="40" placeholder="Add to cart" aria-label="Add to cart label"
                 class="${FIELD_CLASS}" data-field="add-to-cart-label">
          <div class="flex gap-3">
            <input type="text" maxlength="40" placeholder="Cart title" aria-label="Cart title"
                   class="${FIELD_CLASS}" data-field="cart-title">
            <input type="text" maxlength="40" placeholder="Subtotal" aria-label="Cart subtotal label"
                   class="${FIELD_CLASS}" data-field="cart-total-label">
          </div>
          <input type="text" maxlength="40" placeholder="\${{amount}}" aria-label="Money format"
                 class="${FIELD_CLASS} font-mono text-xs" data-field="money-format">
        </div>
      </details>

      <details>
        <summary class="cursor-pointer text-xs font-semibold text-gray-500">Advanced options</summary>
        <div class="mt-3 space-y-2">
          <textarea rows="6" placeholder="{ &quot;product&quot;: { ... } }" aria-label="Advanced options"
                    class="${FIELD_CLASS} font-mono text-xs" data-field="options"></textarea>
          <p class="hidden text-xs text-red-600" data-error="options"></p>
          <p class="text-xs text-gray-400">
            Paste the <code>options</code> object from
            <a href="https://www.shopify.com/buy-button" target="_blank" rel="noopener noreferrer" class="underline">Shopify’s Buy Button generator</a>.
            It overrides the styling above.
          </p>
        </div>
      </details>

      <div class="flex justify-end">
        <button type="button" disabled
                class="relative inline-flex items-center justify-center gap-1 cursor-pointer rounded-md bg-gray-800 px-4 py-2 text-base font-medium text-white hover:bg-gray-900 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                data-done>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3"><polyline points="20 6 9 17 4 12"/></svg>
          <span>Done</span>
        </button>
      </div>
    </div>

    <div class="hidden p-4 space-y-1" data-view="preview">
      <div class="text-sm font-semibold text-gray-900" data-summary-title></div>
      <div class="font-mono text-xs text-gray-500" data-summary-store></div>
    </div>
  </div>
`;

export const ShopifyBuyButton = Node.create({
  name: 'shopifyBuyButton',

  ...blockNodeSpec('shopify_buy_button', {
    attributes: {
      shopifyType: dataAttribute('shopifyType', { default: 'product' }),
      shopifyDomain: dataAttribute('shopifyDomain'),
      shopifyToken: dataAttribute('shopifyToken'),
      shopifyResourceId: dataAttribute('shopifyResourceId'),
      shopifyUrl: dataAttribute('shopifyUrl'),
      shopifyAccentColor: dataAttribute('shopifyAccentColor'),
      shopifyFontFamily: dataAttribute('shopifyFontFamily'),
      shopifyAddToCartLabel: dataAttribute('shopifyAddToCartLabel'),
      shopifyCartTitle: dataAttribute('shopifyCartTitle'),
      shopifyCartTotalLabel: dataAttribute('shopifyCartTotalLabel'),
      shopifyMoneyFormat: dataAttribute('shopifyMoneyFormat'),
      shopifyOptions: dataAttribute('shopifyOptions'),
    },
  }),

  addNodeView() {
    return ({ node, getPos, editor }) => {
      let currentNode = node;

      const dom = document.createElement('div');
      dom.setAttribute('data-shopify-buy-button-node', '');
      dom.innerHTML = EDITOR_HTML;

      const field = name => dom.querySelector(`[data-field="${name}"]`);

      const syncErrors = () => {
        const errors = shopifyFieldErrors(currentNode.attrs);
        dom.querySelectorAll('[data-error]').forEach(el => {
          const message = errors[el.dataset.error];
          el.textContent = message || '';
          el.classList.toggle('hidden', !message);
        });
      };

      // Done needs a store, token and ID the server will accept; styling and options are optional.
      const canFinish = () => {
        const attrs = currentNode.attrs;
        const errors = shopifyFieldErrors(attrs);
        return ['shopifyDomain', 'shopifyToken', 'shopifyResourceId'].every(name => (attrs[name] || '').trim())
          && !errors.domain && !errors.token && !errors['resource-id'];
      };

      const syncSummary = () => {
        const attrs = currentNode.attrs;
        const title = dom.querySelector('[data-summary-title]');
        const store = dom.querySelector('[data-summary-store]');
        if (title) title.textContent = `${attrs.shopifyType === 'collection' ? 'Collection' : 'Product'} ${(attrs.shopifyResourceId || '').trim()}`;
        if (store) store.textContent = (attrs.shopifyDomain || '').trim().toLowerCase();
      };

      const views = blockViews(dom, { canFinish });

      const syncFromNode = () => {
        Object.entries(FIELDS).forEach(([name, attr]) => {
          const el = field(name);
          if (el && el !== document.activeElement) el.value = currentNode.attrs[attr] ?? '';
        });
        syncErrors();
        syncSummary();
        views.refresh();
      };

      const setAttr = (attr, value) => {
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...currentNode.attrs, [attr]: value }));
      };

      Object.entries(FIELDS).forEach(([name, attr]) => {
        field(name)?.addEventListener('input', e => setAttr(attr, e.target.value));
        field(name)?.addEventListener('change', e => setAttr(attr, e.target.value));
      });

      dom.querySelector('[data-remove]')?.addEventListener('mousedown', e => {
        e.preventDefault();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        const current = editor.state.doc.nodeAt(pos);
        if (current) editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
      });

      syncFromNode();
      views.setView(initialView({ hasContent: canFinish() }));

      return {
        dom,
        // Typing in the form fields must not reach ProseMirror (Backspace/Enter would act on the node).
        stopEvent: event => Boolean(event.target?.closest?.('input, select, textarea, button, a, summary')),
        ignoreMutation: () => true,
        update(updatedNode) {
          if (updatedNode.type.name !== 'shopifyBuyButton') return false;
          currentNode = updatedNode;
          syncFromNode();
          return true;
        },
      };
    };
  },

  addCommands() {
    return {
      insertShopifyBuyButton: (attrs = {}) => ({ chain }) =>
        chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});

export const shopifyBuyButtonSlashItem = {
  title: 'Shopify buy button',
  description: 'Sell a product or collection from your Shopify store',
  icon: '🛍',
  group: 'Integrations',
  keywords: ['shopify', 'shop', 'store', 'buy', 'merch', 'product', 'collection', 'cart'],
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).insertShopifyBuyButton().run();
  },
};
